// @ts-nocheck
/**
 * Tenant brand quick-settings — a focused panel to adjust primary/accent
 * colors, upload a logo, and set the footer text with an **instant live
 * preview** of how it renders across the portals (header, tab, footer).
 *
 * Uses the `update_tenant_brand_lite` RPC so school-admins can adjust their
 * own tenant; superadmins can adjust the active tenant they're viewing.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Palette, Save, Upload, Sparkles, Monitor, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useTenantOverride } from "@/lib/tenant-override";
import {
  fetchTenantBranding,
  uploadBrandAsset,
  toEffective,
  type TenantBranding,
} from "@/lib/tenant-branding";
import { applyBrandCss } from "@/lib/tenant-branding";

type Draft = {
  primary_color: string;
  accent_color: string;
  logo_url: string | null;
  footer_text: string;
};

const DEFAULT_DRAFT: Draft = {
  primary_color: "#0f3d2e",
  accent_color: "#c9a84c",
  logo_url: null,
  footer_text: "",
};

function isHex(v: string) {
  return /^#[0-9a-f]{6}$/i.test(v.trim());
}

export function TenantBrandQuickSettings() {
  const { tenantSlug: userSlug, isSuperadmin } = useAuth();
  const { activeTenant, overrideActive } = useTenantOverride();
  const editingSlug = overrideActive ? activeTenant?.slug ?? null : userSlug;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [row, setRow] = useState<TenantBranding | null>(null);
  const [draft, setDraft] = useState<Draft>(DEFAULT_DRAFT);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Load branding row for the current tenant
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (!editingSlug) {
        if (!cancelled) {
          setRow(null);
          setDraft(DEFAULT_DRAFT);
          setLoading(false);
        }
        return;
      }
      const r = await fetchTenantBranding(editingSlug);
      if (cancelled) return;
      setRow(r);
      setDraft(
        r
          ? {
              primary_color: r.primary_color || DEFAULT_DRAFT.primary_color,
              accent_color: r.accent_color || DEFAULT_DRAFT.accent_color,
              logo_url: r.logo_url ?? null,
              footer_text: (r as any).footer_text ?? "",
            }
          : DEFAULT_DRAFT,
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [editingSlug]);

  // Live-apply the draft colors to the page so the whole shell updates as you drag.
  useEffect(() => {
    if (!row) return;
    applyBrandCss(
      toEffective({
        ...row,
        primary_color: draft.primary_color,
        accent_color: draft.accent_color,
        logo_url: draft.logo_url,
      }),
    );
  }, [draft.primary_color, draft.accent_color, draft.logo_url, row]);

  const canEdit = useMemo(
    () => !!editingSlug && (isSuperadmin || !!userSlug),
    [editingSlug, isSuperadmin, userSlug],
  );

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editingSlug) return;
    setUploading(true);
    try {
      const url = await uploadBrandAsset(editingSlug, "logo", file);
      setDraft((d) => ({ ...d, logo_url: url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error("Upload failed", { description: (err as Error).message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editingSlug) return;
    if (!isHex(draft.primary_color)) return toast.error("Primary color must be a #RRGGBB hex");
    if (!isHex(draft.accent_color)) return toast.error("Accent color must be a #RRGGBB hex");
    setSaving(true);
    const { error } = await (supabase as any).rpc("update_tenant_brand_lite", {
      _slug: editingSlug,
      _primary_color: draft.primary_color,
      _accent_color: draft.accent_color,
      _logo_url: draft.logo_url,
      _footer_text: draft.footer_text,
    });
    setSaving(false);
    if (error) {
      toast.error("Save failed", { description: error.message });
      return;
    }
    toast.success("Branding updated across all portals");
    // Refresh cached row so preview reflects saved state
    const fresh = await fetchTenantBranding(editingSlug);
    setRow(fresh);
  }

  if (!editingSlug) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Sign in as a tenant user (or switch into a tenant as superadmin) to edit branding.
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="grid h-40 place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  const brandName = row?.name ?? editingSlug;

  return (
    <Card className="overflow-hidden">
      <div
        className="flex items-center justify-between gap-3 border-b p-4"
        style={{
          background: `linear-gradient(120deg, ${draft.primary_color}12, ${draft.accent_color}18)`,
        }}
      >
        <div className="flex items-center gap-3">
          <span
            className="grid h-11 w-11 place-items-center overflow-hidden rounded-xl text-white shadow-sm"
            style={{
              background: `linear-gradient(135deg, ${draft.primary_color}, ${draft.accent_color})`,
            }}
          >
            {draft.logo_url ? (
              <img src={draft.logo_url} alt={brandName} className="h-full w-full object-contain p-1" />
            ) : (
              <GraduationCap className="h-5 w-5" />
            )}
          </span>
          <div>
            <div className="flex items-center gap-2 font-display text-lg leading-none">
              <Palette className="h-4 w-4" style={{ color: draft.primary_color }} />
              Brand quick-settings
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Editing <span className="font-medium text-foreground">{brandName}</span>
              {isSuperadmin && overrideActive && (
                <Badge variant="outline" className="ml-2 text-[9px]">Superadmin override</Badge>
              )}
            </div>
          </div>
        </div>
        <Button size="sm" disabled={saving || !canEdit} onClick={save}>
          {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
          Save & publish
        </Button>
      </div>

      <div className="grid gap-6 p-4 lg:grid-cols-[1fr_1.1fr]">
        {/* ─── FORM ─── */}
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Primary color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={draft.primary_color}
                onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border bg-transparent p-0"
                aria-label="Pick primary color"
              />
              <Input
                value={draft.primary_color}
                onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })}
                className="h-9 max-w-[140px] font-mono text-xs"
              />
              <span className="text-[11px] text-muted-foreground">buttons, headings, brand marks</span>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Accent color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={draft.accent_color}
                onChange={(e) => setDraft({ ...draft, accent_color: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border bg-transparent p-0"
                aria-label="Pick accent color"
              />
              <Input
                value={draft.accent_color}
                onChange={(e) => setDraft({ ...draft, accent_color: e.target.value })}
                className="h-9 max-w-[140px] font-mono text-xs"
              />
              <span className="text-[11px] text-muted-foreground">highlights, chips, gradients</span>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Logo</Label>
            <div className="mt-1 flex items-center gap-3">
              <div
                className="grid h-14 w-14 place-items-center overflow-hidden rounded-lg border"
                style={{
                  background: `linear-gradient(135deg, ${draft.primary_color}, ${draft.accent_color})`,
                }}
              >
                {draft.logo_url ? (
                  <img src={draft.logo_url} alt="logo preview" className="h-full w-full object-contain p-1" />
                ) : (
                  <Sparkles className="h-5 w-5 text-white/80" />
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onUpload}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="mr-1.5 h-3.5 w-3.5" />
                )}
                {draft.logo_url ? "Replace logo" : "Upload logo"}
              </Button>
              {draft.logo_url && (
                <Button variant="ghost" size="sm" onClick={() => setDraft({ ...draft, logo_url: null })}>
                  Remove
                </Button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              PNG or SVG on a transparent background. Used across every portal, the browser tab,
              and outgoing invites.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Footer text</Label>
            <Textarea
              value={draft.footer_text}
              onChange={(e) => setDraft({ ...draft, footer_text: e.target.value })}
              placeholder="e.g. © 2026 St Mary's Kisubi — All fees safeguarded under Bank of Uganda regulations."
              rows={3}
              className="mt-1 text-sm"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Shown at the bottom of every portal page and at the end of invite messages.
            </p>
          </div>
        </div>

        {/* ─── LIVE PREVIEW ─── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Monitor className="h-3.5 w-3.5" /> Live preview
          </div>
          <div className="overflow-hidden rounded-xl border shadow-sm">
            {/* Header */}
            <div
              className="flex items-center gap-3 border-b p-3"
              style={{
                background: `linear-gradient(120deg, ${draft.primary_color}, ${draft.accent_color})`,
              }}
            >
              <div className="grid h-9 w-9 place-items-center overflow-hidden rounded-lg bg-white/15 backdrop-blur">
                {draft.logo_url ? (
                  <img src={draft.logo_url} alt="" className="h-full w-full object-contain p-1" />
                ) : (
                  <GraduationCap className="h-4 w-4 text-white" />
                )}
              </div>
              <div className="flex-1 text-white">
                <div className="text-sm font-semibold leading-none">{brandName}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-white/70">
                  Parent portal · Dashboard
                </div>
              </div>
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
                Live
              </span>
            </div>

            {/* Body */}
            <div className="space-y-3 bg-background p-4">
              <div className="text-xs uppercase tracking-wide" style={{ color: draft.primary_color }}>
                Today
              </div>
              <div className="font-display text-xl">Welcome back — everything's on track.</div>
              <div className="flex gap-2">
                <button
                  className="rounded-md px-3 py-1.5 text-xs font-medium text-white"
                  style={{ background: draft.primary_color }}
                >
                  Top up wallet
                </button>
                <button
                  className="rounded-md border px-3 py-1.5 text-xs font-medium"
                  style={{ borderColor: draft.accent_color, color: draft.accent_color }}
                >
                  Pay fees
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                {["Wallet", "Fees", "Transport", "Health"].map((k) => (
                  <div key={k} className="rounded-md border p-2">
                    <div
                      className="mb-1 h-1 w-6 rounded"
                      style={{ background: draft.accent_color }}
                    />
                    <div className="font-medium">{k}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div
              className="border-t p-3 text-center text-[11px]"
              style={{
                background: `${draft.primary_color}0d`,
                color: draft.primary_color,
              }}
            >
              {draft.footer_text?.trim() ||
                `© ${new Date().getFullYear()} ${brandName} · Powered by TriSphere`}
            </div>
          </div>

          {/* Tab preview */}
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-2">
            <div
              className="grid h-5 w-5 place-items-center overflow-hidden rounded-sm text-[9px] font-bold text-white"
              style={{
                background: `linear-gradient(135deg, ${draft.primary_color}, ${draft.accent_color})`,
              }}
            >
              {draft.logo_url ? (
                <img src={draft.logo_url} alt="" className="h-full w-full object-contain" />
              ) : (
                (brandName.charAt(0) || "T").toUpperCase()
              )}
            </div>
            <div className="text-xs">
              Dashboard — <span className="font-medium">{brandName}</span>
            </div>
            <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">
              Browser tab
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
